import { useState } from 'react';

import { Alert, Badge, Button, HelpTip, Input, Switch } from '@/components/ui';
import { api } from '@/lib/api';

import { formatarData } from '@/lib/format-date';
/**
 * A IA NA DESCRIÇÃO (mig 564) — a seção da tela do WhatsApp que governa a IA
 * e mostra a fila de espera.
 *
 * 🔴 As decisões do Matheus (09/09), que esta tela apenas obedece:
 *   · a IA reescreve SÓ a "Descrição da atividade" — o resto do molde é fixo;
 *   · mensagem que a IA não respondeu ESPERA, sem prazo — o texto cru só sai
 *     pelo botão "Enviar com o texto original", mensagem a mensagem;
 *   · nasce desligada; ligar exige a chave guardada.
 */

export interface ConfigIa {
  temChaveIa: boolean;
  iaLigada: boolean;
  iaOrientacao: string;
  iaFilaAvisoMin: number;
  iaRecusandoDesdeIso: string | null;
  iaRecusaMotivo: string | null;
}

export interface LinhaFilaIa {
  id: number;
  taskId: string;
  clienteNome: string | null;
  criadoEmIso: string;
  tentativas: number;
  ultimoErro: string | null;
  ultimoErroTipo: string | null;
  textoOriginal: string;
  textoReescrito: string | null;
  estado: string;
  motivoDescarte: string | null;
  resolvidoEmIso: string | null;
}

const AJUDA_IA =
  'Quando ligada, a IA reescreve apenas a "Descrição da atividade" da mensagem de atendimento ' +
  'finalizado — rótulos, data, link e Observações não passam por ela. Se a IA falhar ou ' +
  'demorar, a mensagem espera aqui, sem prazo, e nada sai sozinho com o texto cru: você decide ' +
  'mensagem a mensagem, pelo botão. O texto original fica guardado ao lado do reescrito.';

const AJUDA_ORIENTACAO =
  'O texto que diz à IA COMO reescrever — veio do sistema antigo, exatamente como você o ' +
  'escreveu lá. Pode editar à vontade: vale a partir da próxima mensagem.';

const AJUDA_JANELA =
  'Mensagem esperando a IA além deste tempo gera um aviso no grupo interno de supervisão. ' +
  'O aviso só avisa — não envia nem desiste da mensagem.';

function quando(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : formatarData(d, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function rotuloDoEstado(l: LinhaFilaIa): { texto: string; variant: 'success' | 'warning' | 'danger' | 'neutral' } {
  if (l.estado === 'enviada') return { texto: 'Enviada (reescrita pela IA)', variant: 'success' };
  if (l.estado === 'enviada_original') return { texto: 'Enviada com o texto original', variant: 'success' };
  if (l.estado === 'descartada') return { texto: 'Descartada', variant: 'danger' };
  return { texto: 'Esperando a IA', variant: 'warning' };
}

export function IaNaDescricao({
  cfg,
  fila,
  onMudou,
}: {
  cfg: ConfigIa;
  fila: LinhaFilaIa[];
  onMudou: () => void;
}) {
  const [chave, setChave] = useState('');
  const [orientacao, setOrientacao] = useState<string | null>(null);
  const [janela, setJanela] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erroCampo, setErroCampo] = useState<{ campo: string; texto: string } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const esperando = fila.filter((l) => l.estado === 'esperando');
  const resolvidas = fila.filter((l) => l.estado !== 'esperando');
  const textoOrientacao = orientacao ?? cfg.iaOrientacao;
  const textoJanela = janela ?? String(cfg.iaFilaAvisoMin);

  function falha(err: unknown, campoPadrao: string) {
    const e = err as Error & { response?: { campo?: string; erro?: string } };
    const campo = e.response?.campo ?? campoPadrao;
    const texto = e.response?.erro ?? e.message;
    setErroCampo({ campo, texto });
    document.getElementById(`wa-ia-${campo}`)?.focus();
  }

  async function guardarChave(e: React.FormEvent) {
    e.preventDefault();
    setErroCampo(null);
    setAviso(null);
    setErro(null);
    if (!chave.trim()) {
      // ⚠️ FORMULÁRIO NUNCA É SURDO: erro no campo, com foco.
      setErroCampo({ campo: 'chave', texto: 'Cole a chave da API de IA.' });
      document.getElementById('wa-ia-chave')?.focus();
      return;
    }
    setSalvando('chave');
    try {
      await api.post('/api/v1/admin/whatsapp/chave-ia', { chave: chave.trim() });
      setChave('');
      setAviso('Chave de IA guardada e cifrada. Nada foi ligado — a IA continua como estava.');
      onMudou();
    } catch (err) {
      falha(err, 'chave');
    } finally {
      setSalvando(null);
    }
  }

  async function alternarIa(ligada: boolean) {
    setErro(null);
    setAviso(null);
    try {
      await api.post('/api/v1/admin/whatsapp/ia/ligada', { ligada });
      setAviso(
        ligada
          ? 'IA ligada. A partir de agora a mensagem de OS espera a descrição reescrita antes de sair.'
          : 'IA desligada. Mensagens novas saem direto com o texto do técnico; o que já estava esperando continua na fila, para você decidir.',
      );
      onMudou();
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErro(e.response?.erro ?? e.message);
    }
  }

  async function salvarOrientacao() {
    setErroCampo(null);
    setAviso(null);
    if (!textoOrientacao.trim()) {
      setErroCampo({ campo: 'orientacao', texto: 'A orientação não pode ficar vazia — é ela que segura o tom da IA.' });
      document.getElementById('wa-ia-orientacao')?.focus();
      return;
    }
    /**
     * ⚠️ Medido em 09/09 à noite, com o Matheus na tela: ele colou a MESMA
     * orientação que a migration já semeou e o botão ficava cinza sem dizer
     * por quê — leu-se "não deixa salvar". Texto idêntico ao guardado não é
     * erro: é a resposta "já está salva", dita com todas as letras.
     */
    if (textoOrientacao.trim() === cfg.iaOrientacao.trim()) {
      setOrientacao(null);
      setAviso('Essa orientação JÁ está salva — ela veio preenchida do sistema antigo. Só precisa salvar se editar o texto.');
      return;
    }
    setSalvando('orientacao');
    try {
      await api.post('/api/v1/admin/whatsapp/ia/orientacao', { texto: textoOrientacao });
      setAviso('Orientação salva. Vale a partir da próxima mensagem que a IA reescrever.');
      setOrientacao(null);
      onMudou();
    } catch (err) {
      falha(err, 'orientacao');
    } finally {
      setSalvando(null);
    }
  }

  async function salvarJanela() {
    setErroCampo(null);
    const minutos = Number(textoJanela);
    if (!Number.isInteger(minutos) || minutos <= 0) {
      setErroCampo({ campo: 'janela', texto: 'Informe os minutos (número inteiro).' });
      document.getElementById('wa-ia-janela')?.focus();
      return;
    }
    setSalvando('janela');
    try {
      await api.post('/api/v1/admin/whatsapp/ia/janela', { minutos });
      setAviso('Janela salva.');
      setJanela(null);
      onMudou();
    } catch (err) {
      falha(err, 'janela');
    } finally {
      setSalvando(null);
    }
  }

  async function enviarOriginal(id: number) {
    setErro(null);
    setAviso(null);
    setSalvando(`original-${id}`);
    try {
      const r = await api.post<{ detalhe: string }>(`/api/v1/admin/whatsapp/fila-ia/${id}/enviar-original`, {});
      setAviso(r.detalhe);
      onMudou();
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErro(e.response?.erro ?? e.message);
      onMudou();
    } finally {
      setSalvando(null);
    }
  }

  return (
    <div className="border-t border-line pt-4 dark:border-line-dark" data-testid="wa-ia">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">IA na descrição</h3>
          <HelpTip texto={AJUDA_IA} />
          {!cfg.iaLigada && <Badge variant="neutral" size="sm">Desligada</Badge>}
        </div>
        <Switch
          checked={cfg.iaLigada}
          onChange={(e) => void alternarIa(e.target.checked)}
          label="Ligada"
          data-testid="wa-ia-ligada"
        />
      </div>
      <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
        Reescreve só a “Descrição da atividade” do atendimento finalizado. Se a IA não responder,
        a mensagem espera aqui — nada sai com o texto cru sem você mandar.
      </p>

      {erro && <Alert variant="danger" data-testid="wa-ia-erro">{erro}</Alert>}
      {aviso && <Alert variant="info" data-testid="wa-ia-aviso">{aviso}</Alert>}

      {/* 🔴 "A IA está recusando desde X" — ao lado da fila, como pedido. */}
      {cfg.iaRecusandoDesdeIso && (
        <Alert variant="danger" data-testid="wa-ia-recusando">
          A IA está recusando desde {quando(cfg.iaRecusandoDesdeIso)}.
          {cfg.iaRecusaMotivo ? ` Motivo devolvido pela API: ${cfg.iaRecusaMotivo}` : ''}
          {' '}As mensagens esperam; para soltar alguma, use o botão “Enviar com o texto original”.
        </Alert>
      )}

      {/* A chave — cifrada no cofre; nunca é exibida de volta. */}
      <form onSubmit={guardarChave} className="mt-3 flex flex-wrap items-end gap-3" data-testid="wa-ia-form-chave">
        <div className="min-w-64 flex-1">
          <label htmlFor="wa-ia-chave" className="mb-1.5 block text-sm text-ink-2 dark:text-ink-dark-2">
            Chave da API de IA (Google Gemini)
          </label>
          <Input
            id="wa-ia-chave"
            type="password"
            autoComplete="off"
            value={chave}
            onChange={(e) => setChave(e.target.value)}
            error={erroCampo?.campo === 'chave' ? erroCampo.texto : undefined}
            placeholder={cfg.temChaveIa ? 'Guardada — preencha só para trocar' : 'Cole aqui'}
          />
        </div>
        <Button type="submit" size="sm" loading={salvando === 'chave'} data-testid="wa-ia-salvar-chave">
          Guardar
        </Button>
        <span className="text-xs text-ink-3 dark:text-ink-dark-3">
          {cfg.temChaveIa
            ? 'Guardada e cifrada no cofre.'
            : 'Sem a chave, a IA não liga. Guardar não liga nada.'}
        </span>
      </form>

      {/* A orientação — DADO editável, semeada com o texto do V1. */}
      <div className="mt-3">
        <div className="flex items-center gap-2">
          <label htmlFor="wa-ia-orientacao" className="block text-sm text-ink-2 dark:text-ink-dark-2">
            Orientação da IA
          </label>
          <HelpTip texto={AJUDA_ORIENTACAO} />
        </div>
        <textarea
          id="wa-ia-orientacao"
          value={textoOrientacao}
          onChange={(e) => setOrientacao(e.target.value)}
          rows={8}
          className="mt-1.5 w-full rounded-lg border border-line bg-paper-surface px-3 py-2 text-sm text-ink-1 focus:border-red focus:outline-none dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1"
          data-testid="wa-ia-orientacao"
        />
        {erroCampo?.campo === 'orientacao' && (
          <p className="mt-1 text-xs text-red">{erroCampo.texto}</p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant="secondary"
            loading={salvando === 'orientacao'}
            onClick={() => void salvarOrientacao()}
            data-testid="wa-ia-salvar-orientacao"
          >
            Salvar orientação
          </Button>
          {orientacao === null && (
            <span className="text-xs text-ink-3 dark:text-ink-dark-3">
              Já veio preenchida e salva — é a orientação do sistema antigo. Edite à vontade.
            </span>
          )}
          <div className="flex items-center gap-2">
            <label htmlFor="wa-ia-janela" className="text-xs text-ink-3 dark:text-ink-dark-3">
              Avisar a supervisão se algo esperar mais de
            </label>
            <Input
              id="wa-ia-janela"
              value={textoJanela}
              onChange={(e) => setJanela(e.target.value)}
              error={erroCampo?.campo === 'janela' ? erroCampo.texto : undefined}
              className="w-20"
            />
            <span className="text-xs text-ink-3 dark:text-ink-dark-3">minutos</span>
            <HelpTip texto={AJUDA_JANELA} />
            {janela !== null && (
              <Button size="sm" variant="ghost" loading={salvando === 'janela'} onClick={() => void salvarJanela()}>
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 🔴 A FILA — visível, com o botão que solta na mão. */}
      <div className="mt-4" data-testid="wa-ia-fila">
        <h4 className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          Esperando a IA ({esperando.length})
        </h4>
        {esperando.length === 0 ? (
          <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
            Nenhuma mensagem esperando.
          </p>
        ) : (
          <div className="mt-2 space-y-2">
            {esperando.map((l) => (
              <div
                key={l.id}
                className="rounded-lg border border-line p-3 dark:border-line-dark"
                data-testid={`wa-ia-fila-${l.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                        {l.clienteNome ?? 'Cliente não identificado'}
                      </span>
                      <Badge variant="warning" size="sm">Esperando desde {quando(l.criadoEmIso)}</Badge>
                      <span className="text-xs text-ink-3 dark:text-ink-dark-3">OS {l.taskId}</span>
                    </div>
                    {l.ultimoErro && (
                      <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                        {l.tentativas}ª tentativa —{' '}
                        {l.ultimoErroTipo === 'cota'
                          ? 'recusa por cota: '
                          : l.ultimoErroTipo === 'envio'
                            ? 'o WhatsApp não entregou, tenta de novo sozinha: '
                            : l.ultimoErroTipo === 'envio_esgotado'
                              ? 'o WhatsApp não entregou e as tentativas acabaram: '
                              : 'erro passageiro: '}
                        {l.ultimoErro}
                      </p>
                    )}
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs text-ink-3 dark:text-ink-dark-3">
                        Ver o texto original do técnico
                      </summary>
                      <p className="mt-1 whitespace-pre-wrap text-xs text-ink-2 dark:text-ink-dark-2">
                        {l.textoOriginal}
                      </p>
                    </details>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={salvando === `original-${l.id}`}
                    onClick={() => void enviarOriginal(l.id)}
                    data-testid={`wa-ia-enviar-original-${l.id}`}
                    title="Manda a mensagem agora, com o texto do técnico exatamente como ele escreveu — sem passar pela IA. Passa pelo mesmo porteiro e roteamento de sempre."
                  >
                    Enviar com o texto original
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* O descarte é VISÍVEL: as resolvidas dos últimos 7 dias, com motivo. */}
        {resolvidas.length > 0 && (
          <details className="mt-3" data-testid="wa-ia-resolvidas">
            <summary className="cursor-pointer text-xs text-ink-3 dark:text-ink-dark-3">
              Resolvidas nos últimos 7 dias ({resolvidas.length})
            </summary>
            <div className="mt-2 space-y-2">
              {resolvidas.map((l) => {
                const r = rotuloDoEstado(l);
                return (
                  <div key={l.id} className="rounded-lg border border-line p-3 dark:border-line-dark">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-ink-1 dark:text-ink-dark-1">
                        {l.clienteNome ?? 'Cliente não identificado'}
                      </span>
                      <Badge variant={r.variant} size="sm">{r.texto}</Badge>
                      <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                        OS {l.taskId} · {quando(l.resolvidoEmIso)}
                      </span>
                    </div>
                    {l.motivoDescarte && (
                      <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">Motivo: {l.motivoDescarte}</p>
                    )}
                    {l.textoReescrito && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs text-ink-3 dark:text-ink-dark-3">
                          Comparar original × reescrito
                        </summary>
                        <p className="mt-1 whitespace-pre-wrap text-xs text-ink-2 dark:text-ink-dark-2">
                          <strong>Original:</strong> {l.textoOriginal}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-xs text-ink-2 dark:text-ink-dark-2">
                          <strong>Reescrito:</strong> {l.textoReescrito}
                        </p>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
